import React from 'react';
import ReactCountryFlag from 'react-country-flag';
import Localize from 'react-intl-universal';

import get from 'lodash/get';

import DateRangeIcon from '@mui/icons-material/DateRange';
import SellIcon from '@mui/icons-material/Sell';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { EVENT_STATUS_MAP } from '@common/Constants';
import { countryISOCodeByCountry } from '@common/Countries';
import { formatLongDateTimeStringToDateTimeObject, CourseNameLength } from '@common/Util';
import AudioPreview from '@components/AudioPreview/AudioPreview';

const StyledCardContent = styled(CardContent)(({ props }) => ({
  height: '10rem',
  paddingBottom: 0,
  paddingTop: props?.hasCopy ? 0 : '1rem'
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  background: theme.palette.primary.main,
  color: theme.palette.common.white,
  position: 'absolute',
  top: '0.7rem',
  left: '8px'
}));

const StyledStatusChip = styled(Chip)(() => ({
  position: 'absolute',
  top: '0.7rem',
  right: '8px'
}));

const StyledPrice = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 'bold',
  fontSize: '12px'
}));

const ContentDescriptionName = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'nameLength'
})(({ nameLength }) => ({
  paddingTop: '0.7rem',
  paddingBottom: nameLength > CourseNameLength ? '0.5rem' : '0.4rem'
}));

const EvtNameHeader = styled(Typography)(({ theme }) => ({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontWeight: '600',
  textAlign: 'left',
  lineHeight: 1.2,
  maxWidth: '300px',
  fontSize: '1rem',
  [theme.breakpoints.up('xl')]: {
    fontSize: '1.2rem'
  }
}));

const NUMBER_OF_CARATERS = 40;

const Content = ({
  config,
  eventTypeName = '',
  startDate = '',
  endDate = '',
  eventName = '',
  languages = [],
  price = '',
  currency = '',
  event = {}
}) => {
  return (
    <>
      <StyledCardContent props={{ hasCopy: !!event?.copyrightInfo }}>
        <Box display='flex' flexDirection='column'>
          <Box
            display='flex'
            sx={{
              justifyContent: 'space-between',
              flexWrap: 'wrap'
            }}
          >
            {config?.content?.showMarkLabelInTile && (
              <StyledChip size='small' label={eventTypeName} />
            )}
            {event?.status?.id === EVENT_STATUS_MAP.IN_PREVIEW && (
              <StyledStatusChip
                size='small'
                label={Localize.get('Catalog.upcomingEvent')}
                sx={(theme) => ({
                  backgroundColor: theme.palette.warning.light,
                  color: theme.palette.warning.contrastText
                })}
              />
            )}
            <Typography
              sx={(theme) => ({
                color: theme.palette.primary.main
              })}
            >
              {eventTypeName}
            </Typography>
            <Box display='flex' sx={{ alignItems: 'center' }}>
              {languages.map(
                (lang) =>
                  lang?.value && (
                    <ReactCountryFlag
                      key={lang?.value}
                      countryCode={countryISOCodeByCountry[get(lang, 'key')?.toLowerCase()]}
                      svg
                      alt={lang.value}
                      style={{
                        width: '1.3rem',
                        height: 'auto',
                        marginRight: '1rem'
                      }}
                    />
                  )
              )}

              <SellIcon sx={{ mr: 0.5, fontSize: { xs: '12px', lg: '14px' } }} />
              <StyledPrice>
                {parseInt(price)
                  ? `${price} ${currency} `
                  : Localize.get('CustomerLoyaltyEvents.free')}
              </StyledPrice>
            </Box>
          </Box>
          <ContentDescriptionName nameLength={eventName?.length}>
            <Tooltip
              title={eventName}
              arrow
              disableHoverListener={eventName.length < NUMBER_OF_CARATERS}
            >
              <EvtNameHeader config={config}>{eventName}</EvtNameHeader>
            </Tooltip>
          </ContentDescriptionName>
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ display: 'flex', mb: 0.5 }}>
              <DateRangeIcon fontSize='small' sx={{ mr: 1 }} />
            </Box>
            <Box flex={1}>
              <Typography variant='body2' sx={{ fontSize: { xs: '12px', lg: '13px' } }}>
                {formatLongDateTimeStringToDateTimeObject(startDate)}
              </Typography>
              <Typography variant='body2' sx={{ fontSize: { xs: '12px', lg: '13px' } }}>
                {formatLongDateTimeStringToDateTimeObject(endDate)}
              </Typography>
            </Box>
            <AudioPreview isCatalog={true} url={event?.audioDescription?.url} />
          </Box>
        </Box>
      </StyledCardContent>
    </>
  );
};

export default Content;
